// app_client/src/store/authStore.js
import { create } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const normalizePermissionKey = (value) => String(value || '')
  .trim()
  .toLocaleLowerCase('tr-TR')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/ı/g, 'i')
  .replace(/[^a-z0-9]/g, '');
const accountingRoleKeys = new Set(['muhasebe', 'muhasebemuduru', 'accounting', 'accountant']);
const accountingOperationActions = new Set(['read', 'write', 'assign']);
const accountingDocumentDefinitionModules = new Set([
  'documents',
  'documentdefinition',
  'documentdefinitions',
  'documenttypes',
  'documenttype'
]);
const accountingDocumentDefinitionActions = new Set(['read', 'write']);
const accountingRoleModuleActions = new Set(['read']);

const useAuthStore = create((set, get) => ({
  user: null,
  loading: true,
  error: null,

  // Uygulama ilk açıldığında token kontrolü
  checkAuth: () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          get().logout();
        } else {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          set({ user: decoded, loading: false });
          return;
        }
      } catch { // (err) kısmını tamamen sildik, sadece catch kaldı
        get().logout();
      }
    }
    set({ loading: false });
  },

  login: async (email, password) => {
    set({ error: null });
    try {
      const res = await axios.post('/api/login', { email, password });
      const { token } = res.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const decoded = jwtDecode(token);
      set({ user: decoded, error: null });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Giriş başarısız';
      set({ error: message });
      return { success: false, message };
    }
  },

  logout: () => {
    if (localStorage.getItem('token')) {
      axios.post('/api/logout').catch(() => {});
    }
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    set({ user: null, error: null });
  },

  hasPermission: (moduleName, action = 'read') => {
    const user = get().user;
    if (!user) return false;

    const roleName = normalizePermissionKey(user.roleName);
    if (roleName === 'admin' || roleName === 'superadmin') return true;
    if (
      accountingRoleKeys.has(roleName) &&
      (
        (
          normalizePermissionKey(moduleName) === 'operation' &&
          accountingOperationActions.has(normalizePermissionKey(action))
        ) ||
        (
          accountingDocumentDefinitionModules.has(normalizePermissionKey(moduleName)) &&
          accountingDocumentDefinitionActions.has(normalizePermissionKey(action))
        ) ||
        (
          normalizePermissionKey(moduleName) === 'roles' &&
          accountingRoleModuleActions.has(normalizePermissionKey(action))
        )
      )
    ) {
      return true;
    }

    const permissions = Array.isArray(user.permissions) ? user.permissions : [];
    const normalizedModule = normalizePermissionKey(moduleName);
    const normalizedAction = normalizePermissionKey(action);

    if (!normalizedModule || !['read', 'write', 'delete', 'assign'].includes(normalizedAction)) {
      return false;
    }

    const permission = permissions.find((item) => (
      normalizePermissionKey(item?.module || item?.moduleName) === normalizedModule
    ));

    return Boolean(permission?.[normalizedAction]);
  }
}));

export default useAuthStore;
